import React from 'react'
import WitnessCardCompact from './WitnessCardCompact'
import { useBooksStore } from '../../store/booksStore'
import type { Chapter, Witness } from '../../store/booksStore'
import './HomeChapterDashboard.css'
import './HomeDashboardShared.css'

interface HomeChapterDashboardProps {
    chapterId?: string  // Optional: if provided, show this specific chapter
    onAddWitness?: () => void
    onBookClick?: () => void
}

const HomeChapterDashboard: React.FC<HomeChapterDashboardProps> = ({ chapterId, onAddWitness, onBookClick }) => {
    const { books, getBookChapters, getChapterWitnesses } = useBooksStore()
    const [sortBy, setSortBy] = React.useState<'recent' | 'alpha'>('recent')

    // Find the chapter to display (specific or most recent)
    const getActiveChapter = (): { chapter: Chapter; bookId: string; bookTitle: string; witnesses: Witness[]; timestamp: number } | null => {
        // If a specific chapterId is provided, find it
        if (chapterId) {
            for (const book of books) {
                const chapters = getBookChapters(book.id)
                const chapter = chapters.find(c => c.id === chapterId)

                if (chapter) {
                    const witnesses = getChapterWitnesses(chapter.id)
                    const chapterTimestamp = chapter.created_at || 0
                    const witnessTimestamps = witnesses.map(w => w.last_modified || 0)
                    const maxTimestamp = Math.max(chapterTimestamp, ...witnessTimestamps)

                    return {
                        chapter,
                        bookId: book.id,
                        bookTitle: book.name,
                        witnesses,
                        timestamp: maxTimestamp
                    }
                }
            }
            // Chapter ID not found
            return null
        }

        // Otherwise, find the most recently modified chapter with witnesses
        let mostRecent: { chapter: Chapter; bookId: string; bookTitle: string; witnesses: Witness[]; timestamp: number } | null = null

        for (const book of books) {
            const chapters = getBookChapters(book.id)

            for (const chapter of chapters) {
                const witnesses = getChapterWitnesses(chapter.id)

                if (witnesses.length > 0) {
                    // Use chapter's created_at or the most recent witness modification
                    const chapterTimestamp = chapter.created_at || 0
                    const witnessTimestamps = witnesses.map(w => w.last_modified || 0)
                    const maxTimestamp = Math.max(chapterTimestamp, ...witnessTimestamps)

                    if (!mostRecent || maxTimestamp > mostRecent.timestamp) {
                        mostRecent = {
                            chapter,
                            bookId: book.id,
                            bookTitle: book.name,
                            witnesses,
                            timestamp: maxTimestamp
                        }
                    }
                }
            }
        }

        return mostRecent
    }

    const activeContext = getActiveChapter()

    // Empty state: no witnesses in any chapter
    if (!activeContext) {
        return (
            <div className="home-chapter-dashboard">
                <div className="context-header">
                    <div className="context-breadcrumbs">
                        No Witnesses Yet
                    </div>
                    <div className="context-meta">
                        Drop files to get started
                    </div>
                </div>
            </div>
        )
    }

    const { chapter, bookId, bookTitle, witnesses } = activeContext

    // Sort witnesses
    const sortedWitnesses = [...witnesses].sort((a, b) => {
        if (sortBy === 'alpha') {
            return (a.sigla || '').localeCompare(b.sigla || '')
        }
        // Recent first
        return (b.last_modified || 0) - (a.last_modified || 0)
    })

    // Format timestamp using most recent witness modification
    const getTimestampText = (): string => {
        const now = Date.now()
        // Use the timestamp from getActiveChapter which already calculates the most recent
        const diff = now - activeContext.timestamp

        if (diff < 60000) return 'just now'
        if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`
        if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`
        return `${Math.floor(diff / 86400000)} days ago`
    }

    const handleAddWitness = () => {
        if (onAddWitness) {
            onAddWitness()
        } else {
            // Trigger the file input
            const input = document.getElementById('homepage-file-input') as HTMLInputElement
            input?.click()
        }
    }

    const handleBookClick = () => {
        if (onBookClick) {
            onBookClick()
        }
    }

    return (
        <div className="home-chapter-dashboard">
            <div className="context-header">
                <div className="context-breadcrumbs">
                    <span
                        className="breadcrumb-clickable"
                        onClick={handleBookClick}
                    >
                        {bookTitle}
                    </span>
                    <span className="breadcrumb-divider">›</span>
                    {chapter.name}
                </div>
                <div className="context-meta">
                    {witnesses.length} {witnesses.length === 1 ? 'Witness' : 'Witnesses'} • Last modified {getTimestampText()}
                    <span
                        className="sort-toggle"
                        onClick={() => setSortBy(sortBy === 'recent' ? 'alpha' : 'recent')}
                        title={sortBy === 'recent' ? 'Sort alphabetically' : 'Sort by recent'}
                    >
                        {sortBy === 'recent' ? '↓ Recent' : '↓ A-Z'}
                    </span>
                </div>
            </div>

            {/* Action Bar */}
            <div className="chapter-action-bar" style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                <button
                    className="action-btn"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 16px', background: '#fff', border: '1px solid #e5e7eb',
                        fontSize: '13px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
                        borderRadius: '0' // Sharp Edges
                    }}
                    onClick={() => window.location.hash = `#/books/${bookId}/chapters/${chapter.id}/matrix-edit`}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                    Matrix Editor
                </button>
                <button
                    className="action-btn"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 16px', background: '#fff', border: '1px solid #0891b2',
                        color: '#0891b2', fontSize: '13px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
                        fontWeight: 500, borderRadius: '0'
                    }}
                    onClick={() => window.location.hash = `#/books/${bookId}/chapters/${chapter.id}/analysis`}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                    Analyze Text
                </button>
            </div>

            <div className="witness-grid">
                {/* Add New Card */}
                <div className="witness-card-compact card-add-new" onClick={handleAddWitness}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    <span className="token-label">Add Witness</span>
                </div>

                {/* Witness Cards */}
                {sortedWitnesses.map((witness) => (
                    <WitnessCardCompact
                        key={witness.id}
                        witness={witness}
                        chapterId={chapter.id}
                        bookId={bookId}
                    />
                ))}
            </div>
        </div>
    )
}

export default HomeChapterDashboard
